/**
 * @fileoverview The Stripe subscription management panel — billing cycle,
 * next billing date, cancel and reactivate — plus the hook that loads what it
 * shows.
 *
 * Shared by Settings' plan row (behind its Manage control) and the Dashboard
 * account menu's plan dialog, so cancelling a subscription is one flow with
 * one set of copy wherever it is reached from.
 */

import { useEffect, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/studio/primitives";
import api, { getErrorMessage } from "@/lib/api";
import { isPaidTier, tierDisplayName } from "@/lib/tiers";

/**
 * Whether this account holds a Stripe subscription of its own.
 *
 * Keyed on the STORED tier, deliberately: a grandfathering or founding pin
 * grants entitlement but creates no Stripe subscription to manage.
 */
export const hasStripeSubscription = (user) =>
  user?.subscription_source === "stripe" && isPaidTier(user?.subscription_tier);

/** Whether this account's subscription is billed through the App Store. */
export const hasAppleSubscription = (user) =>
  user?.subscription_source === "apple" && isPaidTier(user?.subscription_tier);

/**
 * Loads `/subscription/stripe/subscription-details` while `enabled`.
 *
 * @param {boolean} enabled
 * @returns {{details: object|null, loading: boolean, setDetails: function}}
 */
export function useStripeDetails(enabled) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    const controller = new AbortController();
    setLoading(true);
    api
      .get("/subscription/stripe/subscription-details", { signal: controller.signal })
      .then((res) => setDetails(res.data))
      .catch((err) => {
        if (!controller.signal.aborted) console.error(err);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [enabled]);

  return { details, loading, setDetails };
}

/**
 * @param {object} props
 * @param {object} props.user The instructor from /auth/me.
 * @param {object|null} props.details The subscription-details payload.
 * @param {boolean} props.loading
 * @param {function} props.onDetails Receives the refreshed payload after a change.
 */
export default function StripeManagePanel({ user, details, loading, onDetails }) {
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const refresh = async () => {
    const res = await api.get("/subscription/stripe/subscription-details");
    onDetails(res.data);
  };

  const cancel = async () => {
    setCancelling(true);
    try {
      const res = await api.post("/subscription/stripe/cancel");
      toast.success(`Subscription will cancel on ${new Date(res.data.cancel_at).toLocaleDateString()}`);
      setConfirming(false);
      await refresh();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to cancel subscription."));
    } finally {
      setCancelling(false);
    }
  };

  const reactivate = async () => {
    try {
      await api.post("/subscription/stripe/reactivate");
      toast.success("Subscription reactivated!");
      await refresh();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to reactivate subscription."));
    }
  };

  if (loading) {
    return (
      <div className="mt-4 flex items-center gap-2 font-sans text-sm text-[var(--text-tertiary)]">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading details…
      </div>
    );
  }
  if (!details?.current_period_end) return null;

  return (
    <div className="mt-4 flex flex-col gap-3 rounded-xl border border-[var(--studio-divider)] bg-[var(--studio-base)] p-4" data-testid="stripe-manage-panel">
      <div className="flex items-center justify-between font-sans text-sm">
        <span className="text-[var(--text-tertiary)]">Billing cycle</span>
        {/* plan is null when the price isn't in the catalogue (X3). It used to
            fall through to "monthly", which told annual subscribers the wrong
            cycle; an em dash says "unknown" instead of rendering nothing. */}
        <span className="capitalize text-[var(--text-primary)]">{details.plan || "—"}</span>
      </div>
      <div className="flex items-center justify-between font-sans text-sm">
        <span className="text-[var(--text-tertiary)]">Next billing date</span>
        <span className="text-[var(--text-primary)]">{new Date(details.current_period_end).toLocaleDateString()}</span>
      </div>
      {details.cancel_at_period_end ? (
        <div className="rounded-xl border border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)] p-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[var(--accent-yellow-text)]" />
            <div>
              <p className="font-sans text-sm font-medium text-[var(--accent-yellow-text)]">Cancellation scheduled</p>
              <p className="mt-1 font-sans text-xs text-[var(--text-secondary)]">
                Your subscription will end on {new Date(details.current_period_end).toLocaleDateString()}.
              </p>
              <Button onPress={reactivate} className="mt-2 px-4 py-1.5 text-xs" data-testid="reactivate-button">
                Reactivate
              </Button>
            </div>
          </div>
        </div>
      ) : confirming ? (
        <div className="rounded-xl border border-[var(--state-danger-border)] bg-[var(--state-danger-dim)] p-3" data-testid="cancel-confirm">
          <p className="mb-3 font-sans text-sm text-[var(--state-danger)]">
            Are you sure? You&apos;ll keep {tierDisplayName(user?.subscription_tier)} until the end of your current billing period.
          </p>
          <div className="flex gap-2">
            <Button onPress={cancel} disabled={cancelling} variant="danger" className="px-4 py-1.5 text-xs" data-testid="confirm-cancel-button">
              {cancelling ? <Loader2 className="h-3 w-3 animate-spin" /> : null} Confirm cancel
            </Button>
            <Button onPress={() => setConfirming(false)} variant="bare" className="px-4 py-1.5 text-xs">
              Keep plan
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <Button onPress={() => setConfirming(true)} variant="bare" className="px-4 py-1.5 text-xs" data-testid="cancel-subscription-button">
            Cancel subscription
          </Button>
        </div>
      )}
    </div>
  );
}
